import { request } from '../../lib/api/request'
import DeviceIncident, {
  DeviceAvailability,
  DeviceIncidentType,
} from '../../model/DeviceIncident'
import { Facility } from '../../reports/ExaminationReportApi'

export type DeviceIncidentFilters = {
  facilityId?: string
  deviceType?: string
  status?: string
  from?: string
  to?: string
}

/**
 * Builds the Mongo selector the `_find` route expects. Kept here rather than in the component so the
 * list screen doesn't have to know the storage shape (notably that `facilityIds` is an array — an
 * equality match against it matches any element, which is what we want).
 */
function toSelector(filters: DeviceIncidentFilters): Record<string, any> {
  const selector: Record<string, any> = {}

  if (filters.facilityId) selector.facilityIds = filters.facilityId
  if (filters.deviceType) selector.deviceType = filters.deviceType
  if (filters.status) selector.status = filters.status

  if (filters.from || filters.to) {
    selector.startTime = {}
    if (filters.from) selector.startTime.$gte = filters.from
    if (filters.to) selector.startTime.$lte = filters.to
  }

  return selector
}

export const deviceIncidentsApi = {
  search(filters: DeviceIncidentFilters = {}, limit = 200) {
    return request<{ docs: DeviceIncident[] }>({
      method: 'POST',
      url: '/device_incidents/_find',
      data: {
        selector: toSelector(filters),
        sort: [{ startTime: 'desc' }],
        limit,
      },
    })
  },

  save(incident: DeviceIncident) {
    return request<DeviceIncident>({
      method: 'POST',
      url: '/device_incidents',
      data: incident,
    })
  },

  /** The device-type vocabulary, served from the server so HealthDrive's labels have one definition. */
  getTypes() {
    return request<{ types: DeviceIncidentType[] }>({
      method: 'GET',
      url: '/device_incidents/types',
    })
  },

  /**
   * All configured facilities — not /reports/facilities, which only returns sites the caller's own
   * patients are placed in. Equipment can break at a site with no current patients, and that
   * incident still has to be recordable.
   */
  getFacilities() {
    return request<{ facilities: Facility[] }>({
      method: 'GET',
      url: '/device_incidents/facilities',
    })
  },

  /**
   * Devices that cannot be ordered at a facility right now. Consumed by the ordering screens, not
   * just this admin area.
   */
  getAvailability(facilityId: string) {
    return request<{ unavailable: DeviceAvailability }>({
      method: 'GET',
      url: '/device_incidents/availability',
      params: { facilityId },
    })
  },
}
